import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ReactivationSettings {
  id: string;
  cadence_days: number;
  attempts_per_cycle: number;
  days_between_attempts: number;
  max_cycles: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  metadata: {
    assigned_sdr_ids?: string[];
    last_assigned_index?: number;
  } | null;
}

export function useReactivationSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['reactivation-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reactivation_settings')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data as ReactivationSettings;
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<ReactivationSettings>) => {
      if (!settings) throw new Error('No settings found');

      const { data, error } = await supabase
        .from('reactivation_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reactivation-settings'] });
      toast.success('Configurações de reativação atualizadas com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar configurações: ${error.message}`);
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
  };
}
